// Minimal RFC 4180-ish CSV parser — handles quoted fields with embedded commas,
// escaped quotes (""), and CRLF line endings. No external deps.
//
// Etsy's exported CSVs use UTF-8 with BOM, comma delimiters, and quoted fields
// that may contain commas (item names, addresses). This handles those cases.

/**
 * Parse a CSV string into an array of objects keyed by header name.
 * Returns { rows: [...], headers: [...], errors: [...] }
 */
export function parseCsv(text) {
  if (!text || typeof text !== 'string') {
    return { rows: [], headers: [], errors: ['No CSV content provided'] };
  }

  // Strip UTF-8 BOM if present
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  const errors = [];
  const lines = parseLines(text);
  if (lines.length === 0) return { rows: [], headers: [], errors: ['Empty CSV'] };

  const headers = lines[0].map(h => (h || '').trim());
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const fields = lines[i];
    // Skip totally empty rows
    if (fields.every(f => !f || !f.trim())) continue;
    const row = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = (fields[j] !== undefined ? fields[j] : '').trim();
    }
    rows.push(row);
  }

  return { rows, headers, errors };
}

// Tokenize the entire CSV into a 2D array of fields.
// Walks the string character-by-character so we correctly handle
// commas and newlines inside quoted fields.
function parseLines(text) {
  const lines = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const len = text.length;

  while (i < len) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          // Escaped quote inside quoted field
          field += '"';
          i += 2;
          continue;
        }
        // End of quoted field
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }

    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }

    if (c === ',') {
      row.push(field);
      field = '';
      i++;
      continue;
    }

    if (c === '\n') {
      row.push(field);
      lines.push(row);
      row = [];
      field = '';
      i++;
      continue;
    }

    if (c === '\r') {
      // Handle CRLF — peek for \n
      row.push(field);
      lines.push(row);
      row = [];
      field = '';
      i++;
      if (text[i] === '\n') i++;
      continue;
    }

    field += c;
    i++;
  }

  // Flush final field/row if file didn't end with newline
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    lines.push(row);
  }

  return lines;
}

/**
 * Try to find a value in a row by checking multiple possible column names.
 * Etsy has changed CSV column names over the years, so we accept variants.
 */
export function pickField(row, candidateNames) {
  for (const name of candidateNames) {
    if (row[name] !== undefined && row[name] !== '') return row[name];
    // Case-insensitive fallback
    const lc = name.toLowerCase();
    for (const key of Object.keys(row)) {
      if (key.toLowerCase() === lc && row[key] !== '') return row[key];
    }
  }
  return '';
}

/**
 * Parse a numeric field that might be formatted as "$1,234.56" or "1234.56"
 * or "1.234,56" (European). Returns NaN if unparseable.
 */
export function parseAmount(value) {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number') return value;
  const s = String(value).trim();
  if (!s) return NaN;
  // Strip currency symbols, whitespace, thousands separators
  const cleaned = s.replace(/[^0-9.,-]/g, '').replace(/,(?=\d{3}(?:\D|$))/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Parse Etsy's date formats: "Jan 15, 2025", "1/15/2025", "2025-01-15", etc.
 * Returns a Date object or null.
 */
export function parseDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d;
  // Try MM/DD/YYYY explicit fallback
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    let year = parseInt(m[3], 10);
    if (year < 100) year += 2000;
    const date = new Date(year, parseInt(m[1], 10) - 1, parseInt(m[2], 10));
    if (!isNaN(date.getTime())) return date;
  }
  return null;
}
